// Ruta /play/multi — mesa de juego con sincronización multiplayer.
//
// Conecta al backend via WebSocket (Sprint 10). El gameStore se
// hidrata con el estado que llega del servidor. Los moves locales
// se envían al servidor, que los aplica y broadcastea el nuevo
// estado al room.

import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { GameBoard } from '../components/GameBoard';
import { ToastViewport } from '../components/ToastViewport';
import { PhaseAnnouncer } from '../lib/useGamePhase';
import { useToastStore } from '../lib/toastStore';
import { connect, disconnect, getMultiplayerStatus, onMultiplayerStatus } from '../lib/multiplayer';

interface MultiSearch {
  room?: string;
  name?: string;
}

export const Route = createFileRoute('/play/multi')({
  component: PlayMultiPage,
  validateSearch: (search: Record<string, unknown>): MultiSearch => {
    const out: MultiSearch = {};
    if (typeof search.room === 'string') out.room = search.room;
    if (typeof search.name === 'string') out.name = search.name;
    return out;
  },
});

function PlayMultiPage(): React.ReactElement | null {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const room: string = search.room ?? '';
  const name: string = search.name ?? 'Anónimo';

  const [status, setStatus] = useState(getMultiplayerStatus());
  const pushToast = useToastStore((s) => s.pushToast);

  useEffect(() => {
    const unsub = onMultiplayerStatus((s, r) => {
      setStatus({ status: s, roomId: r });
      if (s === 'connected') {
        pushToast('info', `Conectado a la sala ${r ?? '?'}`);
      } else if (s === 'disconnected') {
        pushToast('joker-yield', 'Desconectado del servidor');
      }
    });
    return unsub;
  }, [pushToast]);

  useEffect(() => {
    if (!room) {
      void navigate({ to: '/lobby' });
      return;
    }
    void connect(room, name).catch(() => undefined);
    return (): void => {
      disconnect();
    };
  }, [room, name, navigate]);

  if (!room) {
    return null;
  }

  return (
    <main id="main-content" className="min-h-screen bg-bg text-text">
      <div className="border-b border-border bg-bg-alt/40">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 md:px-6">
          <Link to="/lobby" className="text-sm text-text-muted transition hover:text-gold">
            ← Lobby
          </Link>
          <h1 className="font-display text-lg text-gold">
            Regicide · Sala <span className="font-mono text-sm text-text-muted">{room}</span>
          </h1>
          <span
            className={[
              'rounded-full px-2 py-0.5 text-xs font-bold uppercase',
              status.status === 'connected'
                ? 'bg-emerald-700 text-emerald-100'
                : status.status === 'connecting'
                  ? 'bg-yellow-700 text-yellow-100'
                  : 'bg-blood text-text',
            ].join(' ')}
            data-testid="mp-status"
          >
            {status.status}
          </span>
        </div>
      </div>
      <GameBoard />
      <ToastViewport />
      <PhaseAnnouncer />
    </main>
  );
}
